import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import { useRecoilState } from 'recoil';

import type { Organization, PlainObject, Site } from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { type ExpressionError, ExpressionRuntime } from '@gitbook/expr';
import { PlainCodeBlock } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    ActionableContainer,
    Badge,
    Button,
    Description,
    DialogRoot,
    Disclosure,
    DisclosurePanel,
    DisclosureTrigger,
    FieldGroup,
    Group,
    Heading,
    Link,
    type OverlayTriggerState,
    useDialogTriggerState,
} from '@gitbook/spine-react';
import { useLazyRef } from '@gitbook/util-react-hooks';

import { useSetAnalyticsScreen } from '../../../navigation';
import { apiQuery } from '../../../state';
import { ExpressionEditor } from '../../Expressions';
import {
    AdaptiveContentVisitorSegmentsDialog,
    DEFAULT_SEGMENT_TITLE,
} from '../AdaptiveContentVisitorSegmentsDialog';
import { SiteVisitorClaimsButton } from '../SiteVisitorClaimsButton';
import { siteVisitorSegmentSelector, useAdaptiveContentBuilderStateContext } from '../state';
import type { AdaptiveContentBuilderFormValues, AdaptiveContentTargetObjectType } from '../types';
import { useUserDefinedVisitorSegments } from '../useUserDefinedVisitorSegments';
import {
    AdaptiveContentExpressionTemplateSectionSkeleton,
    AdaptiveContentExpressionTestingSectionSkeleton,
    getTextForTarget,
} from './AdaptiveContentExpressionBuilderSkeleton';

interface AdaptiveContentExpressionBuilderProps {
    target: AdaptiveContentTargetObjectType;
}

/**
 * Section of the AdaptiveContentBuilderDialog to build & test an adaptive content expression.
 */
export function AdaptiveContentExpressionBuilder(props: AdaptiveContentExpressionBuilderProps) {
    const { target } = props;

    const { organizationId, siteId, setSelectedTab } = useAdaptiveContentBuilderStateContext();

    const form = useFormContext<AdaptiveContentBuilderFormValues>();
    const { errors } = useFormState({ control: form.control });
    const expression = form.watch('expression') ?? '';

    const runtime = useLazyRef(() => new ExpressionRuntime()).current;
    const [visitorSegment, setVisitorSegment] = useRecoilState(siteVisitorSegmentSelector(siteId));

    useSetAnalyticsScreen(
        analytics.createSiteEvent(
            { id: siteId, organization: organizationId },
            {
                screen: 'adaptive_content_expression',
                adaptiveContentObject: target,
            }
        ),
        [siteId, organizationId]
    );

    const onExpressionChange = (expr: string, error: ExpressionError | null) => {
        form.setValue('expression', expr, { shouldDirty: true });

        if (expr.length === 0) {
            form.clearErrors('expression');
            return;
        }

        if (error) {
            form.setError('expression', {
                type: 'manual',
                message: error.message,
            });
        } else {
            form.clearErrors('expression');
        }
    };

    const onInsertExpressionTemplate = (templateExpr: string) => {
        form.setValue('expression', templateExpr, { shouldDirty: true });
    };

    const evaluationClaims = {
        visitor: { claims: visitorSegment?.claims ?? {} },
    };

    const visitorSegmentsDialogTrigger = useDialogTriggerState();

    return (
        <Disclosure defaultExpanded={true}>
            {({ isExpanded }) => (
                <div className="flex flex-1">
                    <div className="group/expression-builder flex flex-1 flex-col">
                        <div className="flex flex-1 flex-col gap-6 overflow-auto px-10 py-6">
                            <div className="flex flex-col gap-2">
                                <div className="flex items-center gap-2">
                                    <Heading level={2} className="flex-1">
                                        Only show this {getTextForTarget(target)} if:
                                    </Heading>
                                    <DisclosureTrigger>
                                        <Button
                                            kind="blank"
                                            iconOnly
                                            leadingIcon={
                                                isExpanded
                                                    ? Icon.PanelLeftOpen
                                                    : Icon.PanelLeftClose
                                            }
                                            label={isExpanded ? 'Hide examples' : 'Show examples'}
                                            className="opacity-0 focus:opacity-100 group-hover/expression-builder:opacity-100"
                                        />
                                    </DisclosureTrigger>
                                </div>
                                <Description>
                                    Define who can access this {getTextForTarget(target)} by writing
                                    an expression or picking from one of the examples. To use
                                    different properties, edit your{' '}
                                    <Link onPress={() => setSelectedTab('schema')}>
                                        visitor schema
                                    </Link>
                                    .
                                </Description>
                            </div>
                            <div className="rounded-lg border bg-base">
                                <ExpressionEditor
                                    mode="deterministic-expression"
                                    value={expression}
                                    onChange={onExpressionChange}
                                    autoFocus
                                    size="large"
                                    documentationURL={{
                                        path: 'publishing-documentation/adaptive-content',
                                    }}
                                />
                            </div>
                        </div>
                        {!('expression' in errors && errors.expression?.message) ? (
                            <React.Suspense
                                fallback={
                                    <AdaptiveContentExpressionTestingSectionSkeleton
                                        target={target}
                                    />
                                }
                            >
                                <AdaptiveContentExpressionTestingSection
                                    target={target}
                                    organizationId={organizationId}
                                    siteId={siteId}
                                    expression={expression}
                                    runtime={runtime}
                                    evaluatedClaims={evaluationClaims}
                                    visitorSegmentsDialogTrigger={visitorSegmentsDialogTrigger}
                                />
                                <DialogRoot {...visitorSegmentsDialogTrigger.rootProps}>
                                    <AdaptiveContentVisitorSegmentsDialog
                                        organizationId={organizationId}
                                        siteId={siteId}
                                        onConfirm={(selectedSegment) =>
                                            setVisitorSegment(selectedSegment)
                                        }
                                        selectedSegment={visitorSegment ?? undefined}
                                    />
                                </DialogRoot>
                            </React.Suspense>
                        ) : null}
                    </div>
                    <DisclosurePanel
                        orientation="horizontal"
                        className="h-full max-w-[50%] overflow-y-auto bg-side"
                    >
                        <React.Suspense
                            fallback={<AdaptiveContentExpressionTemplateSectionSkeleton />}
                        >
                            <ActionableContainer className="px-10 py-6">
                                <div className="flex items-center">
                                    <Heading>Examples</Heading>
                                    <Description className="ml-auto">
                                        Based on your{' '}
                                        <Link onPress={() => setSelectedTab('schema')}>
                                            visitor schema
                                        </Link>
                                        <Icon.ChevronRight />
                                    </Description>
                                </div>
                                <AdaptiveContentExpressionTemplatesSection
                                    organizationId={organizationId}
                                    siteId={siteId}
                                    onInsertExpressionTemplate={onInsertExpressionTemplate}
                                />
                            </ActionableContainer>
                        </React.Suspense>
                    </DisclosurePanel>
                </div>
            )}
        </Disclosure>
    );
}

/**
 * Section of the adaptive content expression builder showing example/template expressions generated based on site schema.
 */
function AdaptiveContentExpressionTemplatesSection(props: {
    organizationId: Organization['id'];
    siteId: Site['id'];
    onInsertExpressionTemplate: (templateExpr: string) => void;
}) {
    const { organizationId, siteId, onInsertExpressionTemplate } = props;

    const { data: templateConditions } = useSuspenseQuery(
        apiQuery('listSiteAdaptiveTemplateConditions', {
            organizationId,
            siteId,
        })
    );

    return (
        <div className="flex1 flex flex-col gap-4">
            {templateConditions.items.map((template) => (
                <div key={template.condition} className="flex flex-col gap-2">
                    <Description>{template.description}</Description>
                    <PlainCodeBlock
                        syntax="javascript"
                        code={template.condition}
                        toolbar={
                            <Button
                                kind="secondary"
                                size="small"
                                leadingIcon={Icon.Replace}
                                label="Use"
                                onPress={() => onInsertExpressionTemplate(template.condition)}
                                className="opacity-0 focus:opacity-100 group-hover/template:opacity-100"
                            />
                        }
                        showToolbar={true}
                        showCopyButton={false}
                        overflow="wrap"
                        className="group/template [&&]:bg-base"
                    />
                </div>
            ))}
        </div>
    );
}

/**
 * Section of the adaptive content expression builder where the outcome of the expression evaluation is shown.
 */
function AdaptiveContentExpressionTestingSection(props: {
    target: AdaptiveContentTargetObjectType;
    organizationId: Organization['id'];
    siteId: Site['id'];
    expression: string;
    runtime: ExpressionRuntime;
    evaluatedClaims: { visitor: { claims: PlainObject } };
    visitorSegmentsDialogTrigger: OverlayTriggerState;
}) {
    const {
        target,
        expression,
        runtime,
        evaluatedClaims,
        organizationId,
        siteId,
        visitorSegmentsDialogTrigger,
    } = props;

    const [visitorSegment, setVisitorSegment] = useRecoilState(siteVisitorSegmentSelector(siteId));
    const [segmentClaimsShowing, setSegmentClaimsShowing] = React.useState(false);

    const { userDefinedSegments, addVisitorSegment } = useUserDefinedVisitorSegments();
    const { data: autoDetectedSegments } = useSuspenseQuery(
        apiQuery('listSiteVisitorSegments', {
            organizationId,
            siteId,
        })
    );

    if (!userDefinedSegments?.length && !autoDetectedSegments?.items.length) {
        return (
            <div className="flex border-t px-4 py-3">
                <FieldGroup orientation="horizontal">
                    <Group>
                        <Description>
                            You can test a condition’s outcome by comparing it against a snippet of
                            JSON.
                        </Description>
                    </Group>
                    <Link
                        onPress={async () => {
                            const addedSegment = await addVisitorSegment({
                                title: DEFAULT_SEGMENT_TITLE,
                                claims: {},
                            });
                            setVisitorSegment({ type: 'user-defined', ...addedSegment });
                            visitorSegmentsDialogTrigger.open();
                        }}
                    >
                        Add a JSON snippet
                    </Link>
                    <DialogRoot {...visitorSegmentsDialogTrigger.rootProps}>
                        <AdaptiveContentVisitorSegmentsDialog
                            organizationId={organizationId}
                            siteId={siteId}
                            onConfirm={(selectedSegment) => setVisitorSegment(selectedSegment)}
                            selectedSegment={visitorSegment ?? undefined}
                        />
                    </DialogRoot>
                </FieldGroup>
            </div>
        );
    }

    const evaluationOutcome = runtime.evaluateBoolean(expression, evaluatedClaims);

    return (
        <div className="flex flex-col border-t">
            <div className="flex flex-col gap-3 px-4 py-3">
                <div className="flex items-center">
                    <SiteVisitorClaimsButton
                        size="small"
                        organizationId={organizationId}
                        siteId={siteId}
                    />
                    {visitorSegment && (
                        <Button
                            kind="blank"
                            className="ml-auto"
                            label={`${segmentClaimsShowing ? 'Hide' : 'View'} test data`}
                            leadingIcon={Icon.CodeObject}
                            onPress={() => setSegmentClaimsShowing(!segmentClaimsShowing)}
                        >
                            {segmentClaimsShowing ? 'Hide' : 'View'} test data
                        </Button>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <Badge label={evaluationOutcome ? 'TRUE' : 'FALSE'} />
                    <Description>
                        This {target} <b>{evaluationOutcome ? 'would' : "wouldn't"}</b> be
                        accessible to this test user.
                    </Description>
                </div>
            </div>
            {visitorSegment && segmentClaimsShowing ? (
                <div className="-mb-[0.5em] max-h-64 overflow-y-auto border-t">
                    <PlainCodeBlock
                        code={JSON.stringify(evaluatedClaims, null, 2)}
                        syntax="json"
                        lineNumbers
                        overflow="wrap"
                    />
                </div>
            ) : null}
        </div>
    );
}

export function AdaptiveContentExpressionBuilderReadOnly(props: {
    expression: string;
    target: AdaptiveContentTargetObjectType;
}) {
    const { expression, target } = props;
    return (
        <div className="flex flex-col gap-4">
            <Heading level={2}>Only show this {target} if:</Heading>
            <Description>
                This expression defines who can access this page. You can edit it in a change
                request.
            </Description>
            <ExpressionEditor value={expression} onChange={() => {}} readOnly size="large" />
        </div>
    );
}
